// TODO: This page is not part of the official requirements or Figma designs — added for debugging/testing purposes only.
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetClientProfileSettings } from '@/api/clients/get-client-profile-settings'
import { useUpdateClientProfileSettings } from '@/api/clients/update-client-profile-settings'
import { SwitchFormField } from '@/components/form/switch-form-field'
import {
  FieldBooleanBadge,
  FieldCategoryGrid,
  FieldCategoryRow,
} from '@/components/layout/field-row'
import { FieldGroup, FieldGroupHeader, FieldsTable } from '@/components/layout/field-table'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { TabActions, TabCard } from '@/components/layout/tab-card'
import { Badge } from '@/components/ui/badge'
import { BorderLoader } from '@/components/ui/loaders/border-loader'
import { toastFactory } from '@/factories'
import { useFormSave } from '@/hooks'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { getApiErrorMessage } from '@/utils/error'

import {
  composeFormValues,
  composeHiddenFields,
  getResidentProfileFieldSections,
  ResidentProfileSettingsFormValues,
  residentProfileSettingsSchema,
} from './resident-profile-settings.constants'
import * as S from './resident-profile-settings.styled'

export const ResidentProfileSettingsPage = () => {
  const { t } = useTranslation(['building-dashboard'])
  const activeBuilding = useSelector(selectActiveBuilding)
  const buildingId = activeBuilding?.id
  const [isEditing, setIsEditing] = useState(false)
  const fieldSections = getResidentProfileFieldSections(t)

  const { data: settings, isLoading: isProfileSettingLoading } = useGetClientProfileSettings({
    buildingId,
  })
  const { mutateAsync: updateSettings, isPending: isUpdateProfileSettingPending } =
    useUpdateClientProfileSettings()

  const form = useForm<ResidentProfileSettingsFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(residentProfileSettingsSchema),
    reValidateMode: 'onChange',
    values: composeFormValues(settings?.hiddenFields ?? []),
  })

  const isLoading = !buildingId || isProfileSettingLoading || isUpdateProfileSettingPending

  const currentValues = useWatch<ResidentProfileSettingsFormValues>({ control: form.control })
  const initialValues = composeFormValues(settings?.hiddenFields ?? [])

  const handleShowAll = () =>
    fieldSections
      .flatMap((s) => s.fields)
      .forEach(({ name }) => form.setValue(name, true, { shouldDirty: true }))

  const handleHideAll = () =>
    fieldSections
      .flatMap((s) => s.fields)
      .forEach(({ name }) => form.setValue(name, false, { shouldDirty: true }))

  const handleReset = () => form.reset()

  const handleCancel = () => {
    form.reset()
    setIsEditing(false)
  }

  const onSubmit = async (values: ResidentProfileSettingsFormValues) => {
    if (!buildingId) return
    try {
      await updateSettings({ buildingId, hiddenFields: composeHiddenFields(values) })
      toastFactory.success(t('building-dashboard:residentProfileSettings.updateSuccess'))
      setIsEditing(false)
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleSave = useFormSave({ form, onSubmit, onUnchanged: () => setIsEditing(false) })

  return (
    <Page centered fillHeight={false}>
      <PageHeader title={t('building-dashboard:residentProfileSettings.title')} />
      <FormProvider {...form}>
        <TabCard
          titleSuffix={
            isEditing && (
              <S.ActionsRow $disabled={isLoading}>
                <Badge color="green" variant="light" size="small" onClick={handleShowAll}>
                  {t('building-dashboard:residentProfileSettings.actions.showAll')}
                </Badge>
                <Badge color="neutral" variant="light" size="small" onClick={handleHideAll}>
                  {t('building-dashboard:residentProfileSettings.actions.hideAll')}
                </Badge>
                <Badge color="blue" variant="light" size="small" onClick={handleReset}>
                  {t('building-dashboard:residentProfileSettings.actions.reset')}
                </Badge>
              </S.ActionsRow>
            )
          }
          actions={
            <TabActions
              isEditing={isEditing}
              isSaving={isUpdateProfileSettingPending}
              onCancel={handleCancel}
              onEdit={() => setIsEditing(true)}
              onSave={handleSave}
            />
          }
        >
          <S.ContentWrapper>
            {isLoading && (
              <S.LoadingOverlay>
                <BorderLoader size="large" />
              </S.LoadingOverlay>
            )}
            <FieldCategoryGrid>
              {fieldSections.map((section) => (
                <FieldsTable key={section.title}>
                  <FieldGroup>
                    <FieldGroupHeader>{section.title}</FieldGroupHeader>
                    {section.fields.map((field) => (
                      <FieldCategoryRow
                        key={field.name}
                        label={field.label}
                        message={
                          currentValues[field.name] !== initialValues[field.name]
                            ? t('building-dashboard:residentProfileSettings.edited')
                            : undefined
                        }
                      >
                        {isEditing ? (
                          <SwitchFormField name={field.name} disabled={isLoading} size="small" />
                        ) : (
                          <FieldBooleanBadge value={!!currentValues[field.name]} />
                        )}
                      </FieldCategoryRow>
                    ))}
                  </FieldGroup>
                </FieldsTable>
              ))}
            </FieldCategoryGrid>
          </S.ContentWrapper>
        </TabCard>
      </FormProvider>
    </Page>
  )
}
